import AjaxService from '@services/AjaxService';
import BaseRepository from '@repositories/desktop/BaseRepository';
import {convertToOptionsFromCollection} from '@js/functions/form/select';
import SelectOption from '@form/select/SelectOption';
import UrlParameterInterface from '@repositories/UrlParameterInterface';

export default class RecordRepository extends BaseRepository {
  protected baseUrl = '';

  public constructor(protected readonly ajaxService: AjaxService) {
    super(ajaxService)
  }

  public addCostUnitPayment(recordId: number, costUnitId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/cost-units/${costUnitId}/payment/add`,
      {data: {attributes}}
    );
  }

  public addCostUnitPaymentFromSapPayment(costUnitId: number, paymentId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/sappayments/${paymentId}/add/${costUnitId}`,
      {data: {attributes}}
    );
  }

  public addFeePayment(feeId: number, recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/fees/${feeId}/payment/add`,
      {data: {attributes}}
    );
  }

  public addRepairSequencePlanStep(recordId: number, attributes: any, afterStepOrder: number): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/repair-sequence-plan/step/add-after/${afterStepOrder}`,
      {data: {attributes}});
  }

  public addRetroactiveChangesNote(recordId: string, noteData: any): Promise<any> {
    const url = '/api/records/' + recordId + '/retroactive-changes';
    return this.ajaxService.postJson(url, noteData);
  }

  public buildFileToggleVisibilityUrl(recordId: number, fileId: number): string {
    const url = `/api/desktop/records/${recordId}/files/${fileId}/toggle-visibility`;
    return this.ajaxService.addSessionIdQueryParameter(url);
  }

  public buildNoteFileDownloadUrl(recordId: number, noteId: number, fileId: number): string {
    const url = `/api/desktop/records/${recordId}/notes/${noteId}/files/${fileId}/download`;
    return this.ajaxService.addSessionIdQueryParameter(url);
  }

  public buildNoteFilePreviewUrl(recordId: number, noteId: number, fileId: number): string {
    const url = `/api/desktop/records/${recordId}/notes/${noteId}/files/${fileId}`;
    return this.ajaxService.addSessionIdQueryParameter(url);
  }

  public buildSplitThumbnailUrl(recordId: number, fileId: number, pageNumber: number): string {
    const url = `/api/desktop/records/${recordId}/files/${fileId}/split-thumbnails/${pageNumber}`;
    return this.ajaxService.addSessionIdQueryParameter(url);
  }

  public buildStaticAttachmentFilePreviewUrl(recordId: number, fileId: number): string {
    const url = `/api/desktop/records/${recordId}/static-attachment/files/${fileId}`;
    return this.ajaxService.addSessionIdQueryParameter(url);
  }

  public changeSapPaymentState(paymentId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/sappayments/${paymentId}/change-state`);
  }

  public createCallbackNote(recordId: number): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/notes/create-callback`,
      {}
    );
  }

  public createCostUnitPaymentsFromSap(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/sappayments/${recordId}/create-cost-unit-payments`);
  }

  public createExternalServiceProviderAction(recordId: number): Promise<any> {
    return this.ajaxService.getJson(
      `/api/desktop/records/${recordId}/repair-sequence-plan/external-actions/create`
    );
  }

  public createRegressClaimFromCasualty(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/create-regress`,
      {data: {attributes}}
    );
  }

  public createRepairSequencePlanPDF(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/repair-sequence-plan/pdf`,
      {data: {attributes}}
    );
  }

  public deleteCostUnit(recordId: number, costUnitId: number): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/records/${recordId}/cost-units/${costUnitId}`
    );
  }

  public deleteCostUnitPayment(recordId: number, costUnitId: number, costUnitPaymentId: number): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/records/${recordId}/cost-units/${costUnitId}/payments/${costUnitPaymentId}`
    );
  }

  public deleteCostUnitPaymentsForSapPayment(recordId: number, costUnitId: number, costUnitPaymentId: number): Promise<any> {
    return this.ajaxService.sendDelete(`/api/desktop/records/${recordId}/cost-units/${costUnitId}/payments/${costUnitPaymentId}/all-for-sap-payment`);
  }

  public deleteFee(recordId: number, feeId: number): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/records/${recordId}/fees/${feeId}`
    );
  }

  public deleteFeePayment(recordId: number, feeId: number, feePaymentId: number): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/records/${recordId}/fees/${feeId}/payments/${feePaymentId}`
    );
  }

  public deleteNote(recordId: number, noteId: number): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/records/${recordId}/notes/${noteId}`
    );
  }

  public deleteRecord(recordId: number): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/records/${recordId}`
    );
  }

  public deleteRecordFile(recordId: number, fileId: number): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/records/${recordId}/files/${fileId}`
    );
  }

  public deleteRepairSequencePlanStep(recordId: number, attributes: any, stepIdentifier: string): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/repair-sequence-plan/step/delete/${stepIdentifier}`,
      {data: {attributes}});
  }

  public getBasedata(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/basedata`);
  }

  public getExternalServiceProviderActionOptions(recordId: number, externalServiceProviderId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/repair-sequence-plan/external-actions/${externalServiceProviderId}/action-options`
    );
  }

  public getExternalServiceProviderOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/repair-sequence-plan/external-actions/provider-options`
    );
  }

  public getInsuranceBreakdownParentOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/customer/insurances/breakdown-cover/parent-options`
    );
  }

  public getNoteFiles(recordId: number, noteId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/notes/${noteId}/files`);
  }

  public getNoteIndex(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/notes`);
  }

  public getNoteRecipients(recordId: number, noteId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/notes/${noteId}/recipients`);
  }

  public getRepairStatus(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/repair-sequence-plan/status`);
  }

  public hideForGroupMandatoryForCostUnit(recordId: number, urlParameters?: UrlParameterInterface): Promise<any> {
    return this.ajaxService.getJsonWithParameters(
      `/api/desktop/records/${recordId}/hide-for-group-mandatory/cost-units`,
      urlParameters
    );
  }

  public hideForGroupMandatoryForFile(recordId: number, urlParameters?: UrlParameterInterface): Promise<any> {
    return this.ajaxService.getJsonWithParameters(
      `/api/desktop/records/${recordId}/hide-for-group-mandatory/files`,
      urlParameters
    );
  }

  public hideForGroupMandatoryForNote(recordId: number, urlParameters?: UrlParameterInterface): Promise<any> {
    return this.ajaxService.getJsonWithParameters(
      `/api/desktop/records/${recordId}/hide-for-group-mandatory/notes`,
      urlParameters
    );
  }

  public loadAccidentDocumentation(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/accident-documentation`);
  }

  public loadAppraisalDetail(recordId: number, appraisalId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/appraisals/${appraisalId}`);
  }

  public loadAppraiserFirmOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/records/${recordId}/participants/appraiser/firm/options`);
  }

  public loadAppraiserSendOrderMail(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/participants/appraiser/send-order-mail`);
  }

  public loadAppraiserUserOptions(recordId: number, appraiserFirmId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/participants/appraiser/user/options`,
      {appraiserId: appraiserFirmId}
    );
  }

  public loadCarRentalCompanyFirmOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/records/${recordId}/participants/car-rental-company/firm/options`);
  }

  public loadCostUnit(recordId: number, costUnitId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/cost-units/${costUnitId}`);
  }

  public loadCostUnitCreationForm(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/cost-units/create`);
  }

  public loadCostUnitDetail(recordId: number, costUnitId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/cost-units/${costUnitId}/detail`);
  }

  public loadCostUnitDraft(recordId: number, costUnitDraftId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/cost-units/draft/${costUnitDraftId}`);
  }

  public loadCostUnitDraftDetail(recordId: number, costUnitDraftId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/cost-units/draft/${costUnitDraftId}/detail`);
  }

  public loadCostUnitPaymentCreationForm(costUnitId: number, recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/cost-units/${costUnitId}/payments/create`);
  }

  public loadCostUnitPaymentsForSapPayment(recordId: number, costUnitId: number, costUnitPaymentId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/cost-units/${costUnitId}/payments/${costUnitPaymentId}/all-for-sap-payment`);
  }

  public loadCostUnits(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/cost-units`);
  }

  public loadCustomerOverview(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/customer`);
  }

  public loadDataApplicationForm(recordId: string, fileId: string): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/files/${fileId}/klippa/data-application-form`);
  }

  public loadDeliveryFirmOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/accident-documentation/delivery-firm/options`
    );
  }

  public loadDesiredLawyerUserOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/participants/desired-lawyer/user/options`
    );
  }

  public loadDetailerOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/participants/external/detailer/options`
    );
  }

  public loadDifferingRepairFirmOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/records/${recordId}/participants/differing-repair-firm/firm/options`);
  }

  public loadDifferingRepairFirmUserOptions(recordId: number, differingRepairFirmId: number): Promise<any> {
    return this.ajaxService.getJsonWithParameters(
      `/api/desktop/records/${recordId}/participants/differing-repair-firm/user/options`,
      {differingRepairFirm: differingRepairFirmId}
    ).then((response) => {
      return convertToOptionsFromCollection(response.data);
    });
  }

  public loadDocuments(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/documents`);
  }

  public loadDocumentsOverview(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/documents/overview`);
  }

  public loadEditFileForm(recordId: number, fileId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/files/${fileId}/edit`);
  }

  public loadFeeCreationForm(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/fees/create`);
  }

  public loadFeeDetail(feeId: number, recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/fees/${feeId}/detail`);
  }

  public loadFeeEditForm(feeId: number, recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/fees/${feeId}/edit`);
  }

  public loadFees(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/fees`);
  }

  public loadFirmSpecificDocuments(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/firmspecificdocuments`);
  }

  public loadInitialCostUnitPaymentForm(paymentId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/sappayments/${paymentId}/load-cost-unit-payments`);
  }

  public loadKlippaCostUnitData(recordId: number, fileId: number, costUnitId: number): Promise<any> {
    return this.ajaxService.getJson(
      `/api/desktop/records/${recordId}/files/${fileId}/klippa/cost-unit/${costUnitId}/data`
    );
  }

  public loadKlippaCostUnitOptions(recordId: number, fileId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/files/${fileId}/klippa/cost-unit-options`
    );
  }

  public loadLawyerFirmOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/records/${recordId}/participants/lawyer/firm/options`);
  }

  public loadLawyerUserOptions(recordId: number, lawyerFirmId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/participants/lawyer/user/options`,
      {lawyerId: lawyerFirmId}
    );
  }

  public loadLogisticianOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/participants/external/logistician/options`
    );
  }

  public loadMailForm(url: string): Promise<any> {
    return this.ajaxService.getJson(url);
  }

  public loadNoteCreationForm(recordId: number, parentNoteId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/notes/create/${parentNoteId}`);
  }

  public loadNoteUserDetails(recordId: number, noteId: number, userId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/notes/${noteId}/users/${userId}`);
  }

  public loadOverview(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/overview`);
  }

  public loadPaintShopOptions(recordId: number): Promise<any> {
    return this.loadSelectOptions(
      `/api/desktop/records/${recordId}/participants/external/paint-shop/options`
    );
  }

  public loadParticipants(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/participants`);
  }

  public loadPaymentRequests(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/payment-requests`);
  }

  public loadRegressCreateForm(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/create-regress`);
  }

  public loadRegressDataForm(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/regress-data`);
  }
  public loadRepairSequencePlan(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/repair-sequence-plan`);
  }

  public loadResellerUserOptions(recordId: number): Promise<SelectOption[]> {
    const url = `/api/desktop/records/${recordId}/participants/reseller/user/options`;
    return this.ajaxService.getJson(url).then((response) => {
      return convertToOptionsFromCollection(response.data);
    });
  }

  public loadResponsibleUser(recordId: number, userId: number): Promise<any> {
    return this.ajaxService.getJsonWithParameters(
      `/api/desktop/records/${recordId}/responsible-users/${userId}`,
      {'include': ['substitute']}
    );
  }

  public loadSplitFileInfo(recordId: number, fileId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/files/${fileId}/split-info`);
  }

  public loadVehicleOverview(recordId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/records/${recordId}/vehicle`);
  }

  public markNoteComplete(recordId: number, noteId: number): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/notes/${noteId}/mark-complete`,
      {}
    );
  }

  public markPaymentAsAdvice(recordId: number, costUnitId: number, paymentId: number): Promise<any> {
    return this.ajaxService.getJson(
      `/api/desktop/records/${recordId}/cost-units/${costUnitId}/payment/${paymentId}/mark-as-advice`
    )
  }

  public moveSapPaymentToRecord(paymentId: number, recordId: number): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/sappayments/${paymentId}/move/${recordId}`,
      {}
    );
  }

  public reloadAccidentDocumentationBasedata(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/accident-documentation/basedata/reload`,
      {data: {attributes}}
    );
  }

  public reloadCustomerBankAccount(
    recordId: number,
    attributes: any
  ): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/customer/bank-account/reload`,
      {data: {attributes}}
    );
  }

  public reloadCustomerBasedata(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/customer/basedata/reload`,
      {data: {attributes}}
    );
  }

  public reloadRegressData(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/regress-data/reload`,
      {data: {attributes}}
    );
  }

  public reloadVehicleBasedata(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/vehicle/basedata/reload`,
      {data: {attributes}}
    );
  }

  public removeCostUnitPaymentFromSapPayment(subformIdentifier: string, sapPayment: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/sappayments/${sapPayment}/remove/${subformIdentifier}`,
      {data: {attributes}}
    );
  }

  public removeNotePriority(recordId: number, noteId: number): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/notes/${noteId}/remove-priority`,
      {}
    );
  }

  public saveAccidentDocumentationBasedata(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/accident-documentation/basedata/save`,
      {data: {attributes}}
    );
  }

  public saveCostUnitDraft(recordId: number, costUnitDraftId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/cost-units/draft/${costUnitDraftId}/save`,
      {data: {attributes}}
    );
  }

  public saveCostUnitNew(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/cost-units/save`,
      {data: {attributes}}
    );
  }

  public saveCostUnitPayment(costUnitId: number, recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/cost-units/${costUnitId}/payments/save`,
      {data: {attributes}}
    );
  }

  public saveCostUnitPaymentsFromSapPayment(paymentId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/sappayments/${paymentId}/save`,
      {data: {attributes}}
    );
  }

  public saveCostUnitWithPayments(recordId: number, costUnitId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/cost-units/${costUnitId}/save`,
      {data: {attributes}}
    );
  }

  public saveCustomerBankAccount(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/customer/bank-account/save`,
      {data: {attributes}}
    );
  }

  public saveCustomerBasedata(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/customer/basedata/save`,
      {data: {attributes}}
    );
  }

  public saveCustomerInsuranceBreakdownCover(
    recordId: number,
    attributes: any
  ): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/customer/insurances/breakdown-cover/save`,
      {data: {attributes}}
    );
  }

  public saveCustomerInsuranceLegalExpense(
    recordId: number,
    attributes: any
  ): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/customer/insurances/legal-expense/save`,
      {data: {attributes}}
    );
  }

  public saveCustomerInsuranceLiability(
    recordId: number,
    attributes: any
  ): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/customer/insurances/liability/save`,
      {data: {attributes}}
    );
  }

  public saveCustomerNumber(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/customer/customer-number/save`,
      {data: {attributes}}
    );
  }

  public saveDataApplicationForm(recordId: number, fileId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/files/${fileId}/klippa/data-application-form/save`,
      {data: {attributes}}
    );
  }

  public saveEditFileForm(
    recordId: number,
    fileId: number,
    attributes: any
  ): Promise<any> {
    return this.ajaxService.putJson(
      `/api/desktop/records/${recordId}/files/${fileId}`,
      {data: {attributes}}
    );
  }

  public saveExternalServiceProviderAction(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/repair-sequence-plan/external-actions/save`,
      {data: {attributes}}
    );
  }

  public saveFeeNew(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/fees/save`,
      {data: {attributes}}
    );
  }

  public saveFeeWithPayments(feeId: number, recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/fees/${feeId}/save`,
      {data: {attributes}}
    );
  }

  public saveLawsuitData(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/lawsuit/save`,
      {data: {attributes}}
    );
  }

  public saveNoteNew(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/notes/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsAdversary(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/adversary/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsAppraiser(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/appraiser/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsCarRentalCompany(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/car-rental-company/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsCardealer(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/cardealer/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsCasualtyAppraiser(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/casualty-appraiser/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsCasualtyLawyer(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/casualty-lawyer/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsClaimant(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/claimant/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsDefendant(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/defendant/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsDifferingRepairFirm(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/differing-repair-firm/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsDriverAdversary(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/driver-adversary/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsDriverCustomer(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/driver-customer/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsExternal(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/external/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsInjured(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/injured/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsInvolvedPerson(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/involved-person/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsLawyer(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/lawyer/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsPoliceStation(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/police-station/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsPolicyholder(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/policyholder/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsReseller(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/reseller/save`,
      {data: {attributes}}
    );
  }

  public saveParticipantsWitness(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/participants/witness/save`,
      {data: {attributes}}
    );
  }

  public saveRegressData(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/regress-data/save`,
      {data: {attributes}}
    );
  }

  public saveRepairSequencePlan(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/repair-sequence-plan/save`,
      {data: {attributes}}
    );
  }

  public saveVehicleBasedata(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/vehicle/basedata/save`,
      {data: {attributes}}
    );
  }

  public searchRecordByReferenceForSapPayment(paymentId: number, referenceNumber: string): Promise<any> {
    const encoded = encodeURIComponent(referenceNumber);
    return this.ajaxService.getJson(
      `/api/desktop/sappayments/${paymentId}/search-by-reference/${encoded}`
    )
  }

  public sendDocumentMail(url: string, mailFormData: any): Promise<any> {
    return this.ajaxService.postFormDataPlain(url, mailFormData);
  }

  public sendPaymentRequest(recordId: number, recipient: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/payment-requests/send/${recipient}`,
      {data: {attributes}});
  }

  public sendRepairSequencePlanNotification(recordId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/repair-sequence-plan/send`,
      {data: {attributes}}
    );
  }

  public sendSignDocumentsLinkMail(recordId: number, signatureCollectionId: number, recipientEmailAddress: string): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/documents/signature-collection/${signatureCollectionId}/send-mail`,
      {data: {attributes: {recipientEmailAddress}}});
  }

  public showSapPayment(paymentId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/sappayments/${paymentId}/show`);
  }

  public submitSplitDocument(recordId: number, fileId: number, attributes: Record<string, unknown>): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/files/${fileId}/split-document`,
      {data: {attributes}}
    );
  }

  public toggleRecordFileFleetManagerVisibility(recordId: number, fileId: number): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/records/${recordId}/files/${fileId}/toggle-fleet-manager-visibility`,
      {}
    );
  }

  public verifyCostUnit(recordId: number, costUnitId: number): Promise<any> {
    return this.ajaxService.getJson(
      `/api/desktop/records/${recordId}/cost-units/${costUnitId}/verify`
    );
  }
}
